import { FloatingFormItem, InputDecimal } from '@/components/_common';
import {
  TaxeIcmsOriginSelect,
  TaxeIcmsCstSelect,
  TaxeIcmsCsosnSelect,
  TaxePisCstSelect,
  TaxeCofinsCstSelect,
  TaxeIssqnSelect,
} from '@/components/_common/TaxesSelect';
import { useDefaultEmptyFieldError } from '@/hooks';
import { Row, Col, Typography, Form, Checkbox } from 'antd';
import { useInvoiceCFEFormContext } from '../../../InvoiceCFEFormProvider';
import { useCompany } from '@/core/domain/enterprise/Companies/Companies.hooks';
import NcmSelectField from '@/components/_common/NcmSelectField';
import CfopSelectField from '@/components/_common/CfopSelectField';
import { TaxCouponItemCofinsCst } from '@/core/domain/coupon/coupons/coupons.item.types';
import { useNcmMutation } from '@/core/domain/operational/Ncm/Ncm.hooks';
import { useCouponCompanyConfigDetail } from '@/core/domain/coupon/company/company.hooks';
import { CouponItemFormValues } from '..';
import {
  TaxCouponItem,
  TaxCouponItemType,
} from '@/core/domain/coupon/coupons/coupons.types';
import { taxesIcmsCsosnOptions } from '@/components/_common/TaxesSelect/TaxeIcmsCsosnSelect';

interface Props {
  index: number;
}

const InvoiceCFETaxesForm: React.FC<Props> = ({ index }) => {
  const ruleRequired = useDefaultEmptyFieldError();
  const form = Form.useFormInstance<CouponItemFormValues>();
  const formValues = Form.useWatch([], form);

  const hasPart =
    !!formValues?.items?.length &&
    formValues?.items[index].type === TaxCouponItemType.PART;

  const { taxCoupon } = useInvoiceCFEFormContext();

  const { data: company } = useCompany(taxCoupon?.companyId);

  const hasCompanySimpleNational = company?.taxRegimeId === 2;

  const ncmDetail = useNcmMutation();
  const { data: couponCompanyConfig } = useCouponCompanyConfigDetail(
    taxCoupon?.companyId,
  );
  const defaultTaxConfig = couponCompanyConfig?.defaultTaxConfig;

  const taxesCofinsCst = Form.useWatch(
    ['items', index, 'taxes', 'cofins', 'cst'],
    form,
  );

  const optionalCst = [
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_NORMAL_RATE,
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_DIFFERENTIATED_RATE,
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_QUANTITY_RATE,
    TaxCouponItemCofinsCst.TAXABLE_OPERATION_SUBSTITUTION,
    TaxCouponItemCofinsCst.OTHER_OPERATIONS,
  ];

  const cstRequired = !optionalCst.includes(taxesCofinsCst);

  async function handleNcm(ncmId: number) {
    const ncm = await ncmDetail.mutateAsync(ncmId);

    const itemValues = form.getFieldValue(['items', index]) as TaxCouponItem;

    let csosnIcms: undefined | string = ncm?.csosnIcms;
    let cstIcms: undefined | string = ncm?.cstIcms;

    const hasSimpleNational = company?.taxRegimeId === 2;

    if (ncm.csosnIcms) {
      csosnIcms = taxesIcmsCsosnOptions.find(
        (option) => option.label === ncm.csosnIcms,
      )?.value;
    } else {
      csosnIcms = defaultTaxConfig?.partConfig?.icms?.csosn;
    }

    if (ncm.cstIcms) {
      cstIcms = taxesIcmsCsosnOptions.find(
        (option) => option.label === ncm.cstIcms,
      )?.value;
    } else {
      cstIcms = defaultTaxConfig?.partConfig?.icms?.cst;
    }

    form.setFieldValue(['items', index], {
      ...itemValues,
      taxes: {
        ...itemValues.taxes,
        cfopId:
          itemValues.taxes?.cfopId || defaultTaxConfig?.partConfig?.cfopId,
        icms: {
          ...itemValues.taxes?.icms,
          csosn: hasSimpleNational
            ? csosnIcms || itemValues.taxes?.icms?.csosn
            : undefined,
          cst: !hasSimpleNational
            ? cstIcms || itemValues.taxes?.icms?.cst
            : undefined,
          origin:
            defaultTaxConfig?.partConfig?.icms.origin ||
            itemValues.taxes?.icms?.origin,
          rate:
            ncm.aliquotIcms ||
            defaultTaxConfig?.partConfig?.icms.rate ||
            itemValues.taxes?.icms?.rate,
        },
        cofins: {
          ...itemValues.taxes?.cofins,
          cst:
            ncm.cstCofins ||
            defaultTaxConfig?.partConfig?.cofins.cst ||
            itemValues.taxes?.cofins?.cst,
          rate:
            ncm.aliquotCofins ||
            defaultTaxConfig?.partConfig?.cofins.rate ||
            itemValues.taxes?.cofins?.rate,
        },
        pis: {
          ...itemValues.taxes?.pis,
          cst:
            ncm.cstPis ||
            defaultTaxConfig?.partConfig?.pis.cst ||
            itemValues.taxes?.pis?.cst,
          rate:
            ncm.aliquotPis ||
            defaultTaxConfig?.partConfig?.pis.rate ||
            itemValues.taxes?.pis?.rate,
        },
      },
    } as TaxCouponItem);
  }

  return (
    <Row>
      <Col span={24}>
        <Typography.Title level={5}>Geral</Typography.Title>
        <Row gutter={16}>
          <Col span={24} lg={12}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'cfopId']}
              label="CFOP"
              rules={[ruleRequired]}
            >
              <CfopSelectField />
            </FloatingFormItem>
          </Col>

          {hasPart && (
            <Col span={24} lg={12}>
              <FloatingFormItem
                parentFieldName="items"
                name={[index, 'taxes', 'ncmId']}
                label="NCM"
                rules={[ruleRequired]}
                getValueFromEvent={(value) => {
                  handleNcm(value);
                  return value;
                }}
              >
                <NcmSelectField />
              </FloatingFormItem>
            </Col>
          )}

          {!hasPart && (
            <>
              <Col span={24}>
                <Typography.Title level={5}>ISSQN</Typography.Title>

                <Row gutter={[16, 16]}>
                  <Col span={24} lg={12}>
                    <FloatingFormItem
                      parentFieldName="items"
                      name={[index, 'taxes', 'issqn', 'operationType']}
                      label="Tipo de tributação"
                      rules={[ruleRequired]}
                    >
                      <TaxeIssqnSelect />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24} md={6}>
                    <FloatingFormItem
                      parentFieldName="items"
                      name={[index, 'taxes', 'issqn', 'rate']}
                      label="Alíquota(%)"
                      rules={[ruleRequired]}
                    >
                      <InputDecimal />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24} md={6}>
                    <FloatingFormItem
                      parentFieldName="items"
                      name={[index, 'taxes', 'issqn', 'deduction']}
                      label="Dedução(%)"
                      rules={[ruleRequired]}
                    >
                      <InputDecimal />
                    </FloatingFormItem>
                  </Col>

                  <Col span={24} md={6}>
                    <Form.Item
                      name={[index, 'taxes', 'issqn', 'taxIncentive']}
                      rules={[ruleRequired]}
                      valuePropName="checked"
                    >
                      <Checkbox>Incentivo fiscal</Checkbox>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </>
          )}
        </Row>
      </Col>
      <Col span={24}>
        <Typography.Title level={5}>ICMS</Typography.Title>
        <Row gutter={16}>
          <Col span={24} lg={12}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'icms', 'origin']}
              label="Origem"
              rules={[ruleRequired]}
            >
              <TaxeIcmsOriginSelect />
            </FloatingFormItem>
          </Col>

          {!hasCompanySimpleNational && (
            <Col span={24} md={6}>
              <FloatingFormItem
                parentFieldName="items"
                name={[index, 'taxes', 'icms', 'cst']}
                label="CST"
                rules={[ruleRequired]}
              >
                <TaxeIcmsCstSelect />
              </FloatingFormItem>
            </Col>
          )}

          {hasCompanySimpleNational && (
            <Col span={24} md={6}>
              <FloatingFormItem
                parentFieldName="items"
                name={[index, 'taxes', 'icms', 'csosn']}
                label="CSOSN"
                rules={[ruleRequired]}
              >
                <TaxeIcmsCsosnSelect />
              </FloatingFormItem>
            </Col>
          )}

          <Col span={24} md={6}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'icms', 'rate']}
              label="Imposto(%)"
              rules={[ruleRequired]}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Typography.Title level={5}>PIS</Typography.Title>
        <Row gutter={16}>
          <Col span={24} lg={12}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'pis', 'cst']}
              label="CST"
              rules={[ruleRequired]}
            >
              <TaxePisCstSelect />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={6}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'pis', 'baseCalculation']}
              label="Base de cálculo(R$)"
              rules={[ruleRequired]}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={6}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'pis', 'rate']}
              label="Imposto(%)"
              rules={[ruleRequired]}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Typography.Title level={5}>COFINS</Typography.Title>
        <Row gutter={16}>
          <Col span={24} lg={12}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'cofins', 'cst']}
              label="CST"
              rules={[ruleRequired]}
            >
              <TaxeCofinsCstSelect />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={6}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'cofins', 'baseCalculation']}
              label="Base de cálculo(R$)"
              rules={cstRequired ? [ruleRequired] : []}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={6}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'cofins', 'rate']}
              label="Imposto(%)"
              rules={cstRequired ? [ruleRequired] : []}
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>
        </Row>
      </Col>

      {/* <Col span={24}>
        <Typography.Title level={5}>ISSQN</Typography.Title>
        <Row gutter={16}>
          <Col span={24} lg={12}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'issqn', 'exigibility']}
              label="Exigibilidade"
            >
              <TaxeIssqnExigibilitySelect allowClear />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={6}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'issqn', 'deduction']}
              label="Dedução"
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24} md={6}>
            <FloatingFormItem
              parentFieldName="items"
              name={[index, 'taxes', 'issqn', 'rate']}
              label="Imposto(%)"
            >
              <InputDecimal />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Form.Item
              name={[index, 'taxes', 'issqn', 'taxIncentive']}
              valuePropName="checked"
            >
              <Checkbox>Incentivo fiscal</Checkbox>
            </Form.Item>
          </Col>
        </Row>
      </Col> */}
    </Row>
  );
};

export default InvoiceCFETaxesForm;
